import { z } from "zod";

// Valid schedule options for AI reports
export const aiReportScheduleValues = [
	"weekly",
	"monthly",
	"quarterly",
] as const;
export type AiReportSchedule = (typeof aiReportScheduleValues)[number] | null;

export const subjectTypeValues = [
	"BRAND",
	"PERSON",
	"CATEGORY",
	"PRODUCT",
	"LOCATION",
] as const;
export const subjectTypeSchema = z.enum(subjectTypeValues);
export type SubjectType = (typeof subjectTypeValues)[number];

/**
 * Can a property of this subject type have LOCATION children?
 *
 * BRAND, PRODUCT and CATEGORY. All three are market-facing subjects whose
 * answer changes by geography — an energy drink rolling out to new cities needs
 * the same per-market fan-out a multi-location brand does, and a category is
 * tracked per market too: "energy providers for AI data centers" names a
 * different set in Virginia than it does in Texas. PERSON has no geographic
 * sub-structure, and LOCATION itself never nests (locations are a single level,
 * not a tree).
 *
 * This is the single source of truth for the rule. Every site that fans a
 * parent's prompts out to its locations — nightly dispatch, day-1 project runs,
 * prompt-run billing, tracked-prompt budgeting — and the parent-type check in
 * `PropertyService.create` must agree, or a parent gets billed for runs it
 * never makes (or makes runs it never billed for).
 *
 * NOTE: `@repo/payments` cannot import this (it must never depend on
 * `@repo/core`), so the billing paths inline the same three-value check with a
 * comment pointing back here. Keep them in sync.
 */
export function canParentLocations(
	subjectType: string | null | undefined,
): boolean {
	return (
		subjectType === "BRAND" ||
		subjectType === "PRODUCT" ||
		subjectType === "CATEGORY"
	);
}

// The canonical domain regex is exported from ./schemas/property (client-safe);
// kept module-private here to avoid an ambiguous duplicate star re-export from
// the @repo/core barrel.
const domainRegex =
	/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

export const propertySchema = z.object({
	id: z.string(),
	subjectType: subjectTypeSchema,
	domain: z.string().nullable(),
	seedUrl: z.string().nullable(),
	entityRef: z.string().nullable(),
	parentId: z.string().nullable(),
	companyName: z.string().nullable(),
	apiKey: z.string(),
	createdAt: z.date(),
	updatedAt: z.date(),
	verified: z.boolean(),
	verifiedAt: z.date().nullable(),
	organizationId: z.string(),
	blockAiModelTrainers: z.boolean(),
	customBlocks: z.array(z.string()),
	customAllows: z.array(z.string()),
	connectedPlatformType: z.string().nullable(),
	aiReportSchedule: z.enum(aiReportScheduleValues).nullable(),
	aiReportScheduleDayOfWeek: z.number().int().nullable(),
	aiReportScheduleDayOfMonth: z.number().int().nullable(),
	aiReportRecipientEmails: z.array(z.string()),
	// Report section defaults (null = inherit org default, then on)
	defaultShowTechnicalAudit: z.boolean().nullable(),
	defaultShowGroundingSearches: z.boolean().nullable(),
	defaultShowBrandConsistency: z.boolean().nullable(),
	// Default location for AI reports
	locationCity: z.string().nullable(),
	locationRegion: z.string().nullable(),
	locationCountry: z.string().nullable(),
	// Alternate names/aliases for the brand (e.g., ["IBM", "Big Blue"])
	// Used for brand mention detection in AI Visibility Reports
	aliases: z.array(z.string()),
});

export type Property = z.infer<typeof propertySchema>;

export const createPropertySchema = z
	.object({
		subjectType: subjectTypeSchema.default("BRAND"),
		// Required for BRAND subjects; optional otherwise. Validated below via .superRefine().
		domain: z.string().optional(),
		// Required for PERSON, CATEGORY and LOCATION subjects (mapped to
		// companyName for display).
		name: z.string().min(1).max(200).optional(),
		// CATEGORY only: optional free text about the market — who buys in it
		// and what they are choosing between. It has no column; the service
		// seeds it into the property's first BrandSnapshot as `notes[0]`, which
		// is where `resolveCategorySnapshot` reads it back from. Accepted (and
		// silently ignored) on other subject types rather than rejected, so a
		// client that leaves the field on the payload after switching subject
		// type does not get a 400 for a field that does nothing.
		description: z.string().max(2000).optional(),
		seedUrl: z.string().url().optional(),
		entityRef: z.string().optional(),
		parentId: z.string().optional(),
		organizationId: z.string(),
		// LOCATION children populate these at creation time so the geographic
		// context is set up in one round-trip. Harmless on other subject types
		// (the columns are nullable Property defaults).
		locationCity: z.string().optional(),
		locationRegion: z.string().optional(),
		locationCountry: z.string().optional(),
		// Prospecting mode: flag the property as a sales prospect and link it to
		// the bulk-import batch that created it. Harmless on non-prospect creates.
		isProspect: z.boolean().optional(),
		prospectBatchId: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (data.subjectType === "BRAND") {
			if (!data.domain || data.domain.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Domain is required for BRAND subjects",
					path: ["domain"],
				});
			} else if (!domainRegex.test(data.domain)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Please enter a valid domain name",
					path: ["domain"],
				});
			}
		}
		if (
			data.subjectType === "PERSON" &&
			(!data.name || data.name.length === 0)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Name is required for PERSON subjects",
				path: ["name"],
			});
		}
		// A CATEGORY has no domain and no seed URL, so the market's name is its
		// only identity key — without it there is nothing to characterise or to
		// generate prompts about.
		if (
			data.subjectType === "CATEGORY" &&
			(!data.name || data.name.length === 0)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Category name is required",
				path: ["name"],
			});
		}
		if (data.subjectType === "LOCATION") {
			if (!data.parentId || data.parentId.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "parentId is required for LOCATION subjects",
					path: ["parentId"],
				});
			}
			if (!data.name || data.name.length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						"Name is required for LOCATION subjects (used as the location's display label)",
					path: ["name"],
				});
			}
			if (data.domain && data.domain.length > 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						"LOCATION subjects inherit the parent's domain — do not set domain on a location",
					path: ["domain"],
				});
			}
			const hasAnyLocation =
				(data.locationCity && data.locationCity.length > 0) ||
				(data.locationRegion && data.locationRegion.length > 0) ||
				(data.locationCountry && data.locationCountry.length > 0);
			if (!hasAnyLocation) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						"At least one of city, region, or country is required for LOCATION subjects",
					path: ["locationCity"],
				});
			}
		}
	});

// Use the *input* type, not z.infer (output): `subjectType` has a Zod
// `.default()`, so callers may omit it. The API router still runs full schema
// validation before calling the service; direct callers get the lenient type.
//
// The three fields below are deliberately NOT part of `createPropertySchema`:
// they are server-side only, so `POST /properties` (which validates its body
// against the schema) can never set them. Only internal callers — today the
// org-level placement-scoring brand resolver — pass them.
export type CreatePropertyInput = z.input<typeof createPropertySchema> & {
	/**
	 * Create a hidden brand-identity row used only as scoring context: never
	 * listed as a client or a prospect, never scheduled for recurring work.
	 * See `PropertyService.create` for the creation and promotion paths.
	 */
	isScoringOnly?: boolean;
	/**
	 * Curated display name written at creation time. When set on a scoring-only
	 * create it must survive: that branch skips the background name fetch, which
	 * would otherwise overwrite it (and race the scoring job).
	 */
	companyName?: string | null;
	/** Alternate brand names for the mention gate. */
	aliases?: string[];
};

// `subjectType` is deliberately absent. This schema is what the member-auth
// `PATCH /properties/:id` route validates its body against, and
// `PropertyService.update` spreads the validated body straight into Prisma — so
// while it was listed here, any member of the organization could re-type a
// property out from under the surfaces that key off it (and strand its
// domain-derived data). Subject type is chosen at create time. Internal callers
// that legitimately need to change one pass it through `UpdatePropertyInput`
// below, which the route can never produce.
export const updatePropertySchema = z.object({
	id: z.string(),
	companyName: z.string().nullable().optional(),
	logo: z.string().nullable().optional(),
	seedUrl: z.string().url().nullable().optional(),
	entityRef: z.string().nullable().optional(),
	verified: z.boolean().optional(),
	verifiedAt: z.date().nullable().optional(),
	blockAiModelTrainers: z.boolean().optional(),
	customBlocks: z.array(z.string()).optional(),
	customAllows: z.array(z.string()).optional(),
	connectedPlatformType: z.string().nullable().optional(),
	aiReportSchedule: z.enum(aiReportScheduleValues).nullable().optional(),
	aiReportScheduleDayOfWeek: z
		.number()
		.int()
		.min(0)
		.max(6)
		.nullable()
		.optional(),
	aiReportScheduleDayOfMonth: z
		.number()
		.int()
		.min(1)
		.max(28)
		.nullable()
		.optional(),
	aiReportRecipientEmails: z.array(z.string().email()).optional(),
	// Opt-in recipients for wrong-domain consultation alerts. Empty is the
	// deliberate default while the detector calibrates — internal Slack only.
	wrongDomainAlertRecipientEmails: z.array(z.string().email()).optional(),
	// Opt-in recipients for negative key-message alerts. Same default and same
	// reasoning as the list above — empty means nobody is emailed.
	negativeMessageAlertRecipientEmails: z.array(z.string().email()).optional(),
	// Report section defaults (null = inherit org default, then on)
	defaultShowTechnicalAudit: z.boolean().nullable().optional(),
	defaultShowGroundingSearches: z.boolean().nullable().optional(),
	defaultShowBrandConsistency: z.boolean().nullable().optional(),
	// Default location for AI reports
	locationCity: z.string().nullable().optional(),
	locationRegion: z.string().nullable().optional(),
	locationCountry: z.string().nullable().optional(),
	// Monthly billing cap for AI visit tracking (in credits)
	// null = use system default
	aiVisitMonthlyMaxCredits: z.number().int().min(0).nullable().optional(),
	// Alternate names/aliases for the brand
	aliases: z.array(z.string()).optional(),
	// Sales-prospect / exploratory property. Prospects are excluded from the
	// Clients grid and skipped by scheduled background work (weekly maintenance,
	// monthly brand consistency checks). Flipping this converts a property
	// between "active" and "prospect" in either direction.
	isProspect: z.boolean().optional(),
});

export type UpdatePropertyInput = z.infer<typeof updatePropertySchema> & {
	/**
	 * Server-side only, exactly like the extra fields on `CreatePropertyInput`:
	 * not part of `updatePropertySchema`, so no HTTP body can carry it into
	 * `PropertyService.update`. Only an internal/admin caller can set it.
	 */
	subjectType?: SubjectType;
};
